import { Direction, Toast, Tooltip } from 'radix-ui';
import { createContext, type ReactNode, useContext } from 'react';

/**
 * The few words shared components need (close, pagination...). The app passes them from its
 * translation catalogs: this package never contains user-facing text of its own.
 */
export interface UiLabels {
  readonly close: string;
  readonly loading: string;
  readonly previousPage: string;
  readonly nextPage: string;
  readonly pageSummary: (page: number, totalPages: number, total: number) => string;
  readonly sortAscending: string;
  readonly sortDescending: string;
  readonly retry: string;
  readonly openNavigation: string;
  readonly collapseNavigation: string;
  readonly expandNavigation: string;
  readonly cancel: string;
}

interface UiContextValue {
  readonly dir: 'ltr' | 'rtl';
  readonly labels: UiLabels;
}

const UiContext = createContext<UiContextValue | null>(null);

export function UiProvider(props: { dir: 'ltr' | 'rtl'; labels: UiLabels; children: ReactNode }) {
  return (
    <UiContext.Provider value={{ dir: props.dir, labels: props.labels }}>
      <Direction.Provider dir={props.dir}>
        <Tooltip.Provider delayDuration={300}>
          <Toast.Provider swipeDirection={props.dir === 'rtl' ? 'left' : 'right'} duration={5000}>
            {props.children}
          </Toast.Provider>
        </Tooltip.Provider>
      </Direction.Provider>
    </UiContext.Provider>
  );
}

export function useUi(): UiContextValue {
  const value = useContext(UiContext);
  if (!value) throw new Error('Wrap the app in <UiProvider>');
  return value;
}
